import React from 'react';
import { Figure } from '@/components/ui/Figure';
import { formatDecimalString } from '@/utils/decimal-display';
import { decimalStringsDiffer } from '../utils/paid-discrepancy';
import { columnDp, MIN_DP } from '../utils/column-dp';
import type {
  CashPoolBlock,
  CashPoolDocument,
  CashPoolPosting,
  CashPoolRow,
  CashPoolSection,
} from '@/types';

interface Props {
  document: CashPoolDocument;
  /**
   * Page-only controls (Mark paid strip, note editor), injected the way
   * `StatementDocumentView` takes `renderBlockExtras` — so this stays a
   * view-only renderer usable from both the document page and the list's
   * expandable row. Called PER BLOCK: a currency is what settles, so each block
   * has its own statement, its own status and its own Mark paid.
   */
  renderBlockExtras?: (block: CashPoolBlock) => React.ReactNode;
  /**
   * 'sheet' is the full invoice on the document page; 'compact' is the inline
   * slice under an expanded list row, which already sits inside its own panel
   * and must not repeat the masthead.
   */
  variant?: 'sheet' | 'compact';
  /**
   * Month-to-date draft rather than a generated statement (LR-624). Passed by
   * the caller that knows which document it asked for, NOT derived from the
   * data: a real preview can hold no accrual at all - the 1st of the month, or
   * a counterparty whose fixings have not published - and still carry the
   * period's postings. The export marks that same document `PREVIEW`, so
   * deriving it here made the downloaded file and the screen disagree about
   * what the reader was looking at. Also gates each pool's `data_through`
   * (LR-654): a closed month carries no freshness mark.
   */
  preview?: boolean;
}

interface Column {
  label: string;
  align: 'left' | 'right';
  width: string;
  render: (row: CashPoolRow, dp: number) => React.ReactNode;
  /** Stored precision — the most fraction digits this column may ever print. */
  maxDp?: number;
  /** The column's value, for measuring how many of those digits are significant. */
  get?: (row: CashPoolRow) => string | null;
  aria?: (row: CashPoolRow, dp: number) => string;
}

const figureColumn = (
  label: string,
  width: string,
  get: (row: CashPoolRow) => string | null,
  maxDp = 2
): Column => ({
  label,
  align: 'right',
  width,
  maxDp,
  get,
  render: (r, dp) => (
    <Figure value={get(r)} dp={dp} tier="inherit" demoteDecimals={false} />
  ),
  aria: (r, dp) => formatDecimalString(get(r), { dp }),
});

/**
 * A line's date cell: the single day, or the span it covers.
 *
 * Unlike the trade statement — where product made every calendar day its own
 * line — the pool balance only moves when a margin call is posted, so a line
 * legitimately covers several days at one figure. Printing only the first date
 * beside "3" would leave the reader to work out where the run ends.
 */
// En dash, matching the masthead period below and the expanded row's date span:
// one printed statement should not use two range glyphs.
const dateCell = (row: CashPoolRow): string =>
  row.days <= 1 ? row.start_date : `${row.start_date} – ${row.end_date}`;

// Feedback #74, verbatim: "Date, Collateral Value (with margin), Rate, Number of
// Days, Interest Amount, benchmark, spread (if applicable), Currency". Ordered
// to read left-to-right as the calculation the counterparty can check —
// value × rate ÷ day-count × days = amount — with the rate's provenance beside
// it. Currency is on the block heading rather than repeated on every line: a
// block IS one currency.
const COLUMNS: Column[] = [
  { label: 'Date', align: 'left', width: '176px', render: dateCell },
  figureColumn('Collateral Value', '132px', (r) => r.collateral_value),
  // '%' in the label: rate is a percent by the LR-346 convention (3.65 = 3.65%).
  figureColumn('Rate %', '86px', (r) => r.rate, 6),
  {
    label: 'Days',
    align: 'right',
    width: '64px',
    render: (r) => r.days,
  },
  {
    label: 'Benchmark',
    align: 'left',
    width: '92px',
    render: (r) => (r.benchmark ? r.benchmark.toUpperCase() : '—'),
  },
  // Blank on a fixed pool and on the implicit repo pool; a spread is half of what
  // a rate definition can be (Mirae R8.2: benchmark ± spread OR fixed).
  figureColumn('Spread', '78px', (r) => r.spread, 6),
  figureColumn('Interest', '116px', (r) => r.interest_amount, 6),
];

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  // No app action sets it (LR-681), but an admin edit can; don't print it raw.
  issued: 'Issued',
  paid: 'Paid',
  // LR-624: the unpersisted month-to-date document's blocks.
  preview: 'Preview',
};

const HeaderLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <dt className="text-[10.5px] font-semibold uppercase tracking-[0.05em] text-lr-muted">
    {children}
  </dt>
);

const Pill: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="inline-flex items-center rounded-full bg-lr-accent-weak px-2 py-px text-[11px] font-semibold text-lr-accent">
    {children}
  </span>
);

/** Day-count bases a pool's lines carry, e.g. `ACT/360`. */
const dayCountLabel = (rows: CashPoolRow[]): string =>
  [...new Set(rows.map((r) => r.day_count_basis))]
    .sort((a, b) => a - b)
    .map((b) => `ACT/${b}`)
    .join(', ');

const PoolSection: React.FC<{
  section: CashPoolSection;
  dps: number[];
  preview: boolean;
}> = ({ section, dps, preview }) => (
  <tbody className="bg-lr-surface">
    <tr className="border-b border-lr-border bg-lr-surface-2">
      {/* `rowgroup`, not `colgroup`: this heading names the pool whose lines
          follow it inside THIS tbody. The table has one <colgroup> spanning
          every column, so `colgroup` would map the pool name onto the whole
          table as a column header — and in a two-pool block a screen reader
          would announce each line under both pools' names, which on an invoice
          says a line accrued somewhere it did not. */}
      <th
        scope="rowgroup"
        colSpan={COLUMNS.length - 1}
        className="px-3 py-[7px] text-left text-[12px] font-semibold text-lr-fg"
      >
        {section.label}
        {/* The divisor the printed arithmetic needs, beside the pool it belongs
            to: accrual days logic is a per-pool contractual term (LR-580), so
            two pools in one currency can legitimately differ by ~1.4 %.
            A real space before each span, not only the margin: the heading's
            accessible name joins inline siblings without one
            ("(GMRA)ACT/360Last accrual"). */}{' '}
        <span className="ml-1 font-mono text-[11px] font-normal text-lr-muted">
          {dayCountLabel(section.rows) || '—'}
        </span>
        {/* Freshness per pool, not per document (LR-654): two pools in one
            currency can stop on different days, and neither may vouch for the
            other. "Last accrual", the export's label, not "accrued through":
            a day whose fixing never published leaves a gap in the lines, and
            "through" would claim it. */}
        {preview && section.data_through && (
          <>
            {' '}
            <span className="ml-1 text-[11px] font-normal text-lr-muted">
              Last accrual {section.data_through}
            </span>
          </>
        )}
      </th>
      <td className="px-3 py-[7px] text-right">
        <Figure
          value={section.total}
          dp={2}
          tier="inherit"
          className="font-semibold"
        />
      </td>
    </tr>
    {section.rows.map((r) => (
      <tr
        key={`${section.pool_id ?? 'bridge'}-${r.start_date}`}
        className="border-b border-lr-border-soft last:border-b-0 hover:bg-lr-row-active"
      >
        {COLUMNS.map((c, ci) => (
          <td
            key={c.label}
            aria-label={c.aria?.(r, dps[ci])}
            className={`px-3 py-[7px] ${
              c.align === 'right' ? 'text-right' : 'truncate text-lr-fg-2'
            }`}
          >
            {c.render(r, dps[ci])}
          </td>
        ))}
      </tr>
    ))}
  </tbody>
);

// The postings column set (LR-624, feedback п.9a): the period's repo margin
// call events, verbatim off each call row. Mirrors the export's column set
// exactly, `_CASH_POOL_POSTINGS_HEADER`. Currency is ON the line, unlike the
// interest table - postings are one flat document-level section and a call
// settles in its own currency (LR-493); `Book` is the netting book's trade
// currency, without which two books settling in one currency interleave into a
// Before/After column that reads as a broken series. No total row: a posting is
// not interest, and rows may mix settlement currencies, which do not add up.
const POSTING_COLUMNS: {
  label: string;
  align: 'left' | 'right';
  width: string;
  render: (p: CashPoolPosting) => React.ReactNode;
}[] = [
  { label: 'Date', align: 'left', width: '110px', render: (p) => p.posted_on },
  {
    label: 'Book',
    align: 'left',
    width: '76px',
    render: (p) => <span className="font-mono">{p.book}</span>,
  },
  {
    label: 'Posted Amount',
    align: 'right',
    width: '140px',
    render: (p) => <Figure value={p.amount} dp={2} tier="inherit" />,
  },
  {
    label: 'Currency',
    align: 'left',
    width: '90px',
    render: (p) => <span className="font-mono">{p.currency}</span>,
  },
  {
    label: 'Book Before',
    align: 'right',
    width: '140px',
    render: (p) => <Figure value={p.book_before} dp={2} tier="inherit" />,
  },
  {
    label: 'Book After',
    align: 'right',
    width: '140px',
    render: (p) => <Figure value={p.book_after} dp={2} tier="inherit" />,
  },
];

const PostingsSection: React.FC<{ postings: CashPoolPosting[] }> = ({
  postings,
}) => (
  <section className="mt-6">
    {/* Wording kept identical to the export's, down to the case: an operator
        diffing a downloaded CSV against the screen reads any difference as a
        version skew. */}
    <h2 className="mb-1 text-[13px] font-bold">Repo margin call postings</h2>
    {/* Same legend the export prints under this heading. It says REPO because
        only a GMRA pool can take a margin call posting (`_ENTRY_PRODUCT`); a
        GMSLA pool is funded by its trade bookings, so its Collateral Value has
        no counterpart here and must not be claimed to have one. */}
    <p className="mb-2 text-[11px] text-lr-muted">
      Posted Amount, Book Before and Book After are signed from our perspective
      - positive = we hold the counterparty cash. They are the repo netting
      book&apos;s own collateral recorded at posting time, read down within one
      Book; a GMSLA pool is funded by its trade bookings and is not represented
      here.
    </p>
    <div className="overflow-x-auto rounded-[8px] border border-lr-border">
      <table className="w-full table-fixed text-[13px]">
        <caption className="sr-only">
          Repo margin call postings within the statement period
        </caption>
        <colgroup>
          {POSTING_COLUMNS.map((c) => (
            <col key={c.label} style={{ width: c.width }} />
          ))}
        </colgroup>
        <thead className="bg-lr-surface-2">
          <tr className="border-b border-lr-border">
            {POSTING_COLUMNS.map((c) => (
              <th
                key={c.label}
                scope="col"
                className={`px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.03em] text-lr-muted ${
                  c.align === 'right' ? 'text-right' : 'text-left'
                }`}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-lr-surface">
          {postings.map((p, i) => (
            <tr
              key={`${p.book}-${p.posted_on}-${i}`}
              className="border-b border-lr-border-soft last:border-b-0 hover:bg-lr-row-active"
            >
              {POSTING_COLUMNS.map((c) => (
                <td
                  key={c.label}
                  className={`px-3 py-[7px] ${
                    c.align === 'right' ? 'text-right' : 'text-lr-fg-2'
                  }`}
                >
                  {c.render(p)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </section>
);

const CurrencyBlockView: React.FC<{
  block: CashPoolBlock;
  counterpartyName: string;
  periodStart: string;
  periodEnd: string;
  renderBlockExtras?: (block: CashPoolBlock) => React.ReactNode;
  preview: boolean;
}> = ({
  block,
  counterpartyName,
  periodStart,
  periodEnd,
  renderBlockExtras,
  preview,
}) => {
  const rows = block.sections.flatMap((s) => s.rows);

  // One fraction width per column across the whole block, so a single long rate
  // does not pad every round figure out to six zeros.
  const dps = COLUMNS.map((c) =>
    c.maxDp == null || c.get == null
      ? MIN_DP
      : columnDp(rows.map(c.get), c.maxDp)
  );

  // The persisted total is authoritative; the live sum is carried beside it so
  // an out-of-band drift is visible rather than silent (same contract the trade
  // document holds between `total_accrued` and `grand_total`). Compared by
  // VALUE: `!==` on the rendered strings fires on a signed zero or a
  // number-typed field and accuses a statement that reconciles exactly.
  const drifted = decimalStringsDiffer(block.total, block.recomputed_total);

  return (
    <section className="mt-6 first:mt-0">
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <h2 className="flex items-center gap-2 text-[13px] font-bold">
          <span className="font-mono">{block.currency}</span>
          <Pill>{STATUS_LABELS[block.status] ?? block.status}</Pill>
        </h2>
        <Figure
          value={block.total}
          currency={block.currency}
          softDecimals
          className="font-semibold"
        />
      </div>

      {renderBlockExtras?.(block)}

      {/* Above the table, not inside it: the loudest drift of all is no lines at
          all beside a non-zero total, and that renders the empty state. */}
      {drifted && (
        <p className="mt-2 text-[11.5px] text-lr-warn" role="status">
          The statement total (
          {formatDecimalString(block.total, { currency: block.currency })})
          differs from the sum of the lines shown (
          {formatDecimalString(block.recomputed_total, {
            currency: block.currency,
          })}
          ). Regenerate the statement to reconcile it.
        </p>
      )}

      {rows.length === 0 ? (
        <div className="rounded-[8px] border border-dashed border-lr-border-2 px-4 py-8 text-center text-[12.5px] text-lr-faint">
          No cash pool interest accrued this period.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-[8px] border border-lr-border">
          <table className="w-full table-fixed text-[13px]">
            <caption className="sr-only">
              Cash pool interest for {counterpartyName} ({block.currency}),{' '}
              {periodStart} to {periodEnd}
            </caption>
            <colgroup>
              {COLUMNS.map((c) => (
                <col key={c.label} style={{ width: c.width }} />
              ))}
            </colgroup>
            <thead className="bg-lr-surface-2">
              <tr className="border-b border-lr-border">
                {COLUMNS.map((c) => (
                  <th
                    key={c.label}
                    scope="col"
                    className={`px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.03em] text-lr-muted ${
                      c.align === 'right' ? 'text-right' : 'text-left'
                    }`}
                  >
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            {block.sections.map((section) => (
              <PoolSection
                key={section.pool_id ?? section.label}
                section={section}
                dps={dps}
                preview={preview}
              />
            ))}
            <tfoot>
              <tr className="border-t border-lr-border bg-lr-surface-2">
                <td
                  colSpan={COLUMNS.length - 1}
                  className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-[0.03em] text-lr-muted"
                >
                  Total {block.currency}
                </td>
                <td className="px-3 py-2 text-right">
                  <Figure
                    value={block.total}
                    currency={block.currency}
                    softDecimals
                    className="font-semibold"
                  />
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </section>
  );
};

/**
 * The Cash Pool Interest Statement (LR-558) — interest accrued on a
 * counterparty's pooled cash for one month, every pool of theirs in the one
 * document (product answer #15) and every currency as its own block.
 *
 * Deliberately a separate renderer from `StatementDocumentView`: this document
 * has no positions and its columns are its own. Forcing it through the trade
 * renderer would print an empty statement.
 *
 * Sign convention, shared with the rest of billing: a NEGATIVE figure is due to
 * the counterparty. Holding their cash means owing them interest, so a positive
 * pool balance shows here as a negative collateral value and a negative amount.
 *
 * No cross-currency total anywhere, on purpose — two currencies do not add up,
 * the same rule the trade document holds.
 */
export const CashPoolDocumentView: React.FC<Props> = ({
  document,
  renderBlockExtras,
  variant = 'sheet',
  preview = false,
}) => (
  <div className="text-lr-fg">
    {/* MTD preview only (LR-624): the nightly engine writes yesterday's
        interest at 23:00 UTC, so an intraday reader must be told the figures
        stop at the last posted day rather than "today". No date here (LR-654):
        how far the accruals reach is per pool, under each pool heading - one
        date over several pools is wrong one way or the other. */}
    {preview && (
      <p className="mb-4 text-[11.5px] text-lr-warn" role="status">
        Month-to-date preview. Interest posts nightly.
      </p>
    )}
    {variant === 'sheet' && (
      <dl className="mb-6 grid grid-cols-2 gap-x-8 gap-y-2.5 border-b-2 border-lr-fg pb-4 text-[12.5px]">
        <div className="flex items-baseline justify-between gap-3">
          <HeaderLabel>Counterparty</HeaderLabel>
          <dd className="font-semibold">{document.counterparty_name}</dd>
        </div>
        <div className="flex items-baseline justify-between gap-3">
          <HeaderLabel>Period</HeaderLabel>
          <dd className="font-medium">
            {document.period_start} – {document.period_end}
          </dd>
        </div>
        <div className="flex items-baseline justify-between gap-3">
          <HeaderLabel>Statement</HeaderLabel>
          <dd className="font-medium">Cash Pool Interest</dd>
        </div>
        <div className="flex items-baseline justify-between gap-3">
          <HeaderLabel>Pools</HeaderLabel>
          <dd className="font-medium">
            {document.blocks.reduce((n, b) => n + b.sections.length, 0)} in{' '}
            {document.blocks.length}{' '}
            {document.blocks.length === 1 ? 'currency' : 'currencies'}
          </dd>
        </div>
      </dl>
    )}

    {document.blocks.length === 0 ? (
      <div className="rounded-[8px] border border-dashed border-lr-border-2 px-4 py-8 text-center text-[12.5px] text-lr-faint">
        No cash pool interest accrued this period.
      </div>
    ) : (
      document.blocks.map((block) => (
        <CurrencyBlockView
          key={block.statement_id ?? block.currency}
          block={block}
          counterpartyName={document.counterparty_name}
          periodStart={document.period_start}
          periodEnd={document.period_end}
          renderBlockExtras={renderBlockExtras}
          preview={preview}
        />
      ))
    )}

    {/* Guarded on presence, not just length: a document from a not-yet-rolled
        backend has no `postings` key, and an absent section must not crash.
        Sheet variant only: the compact variant is ONE currency's slice of the
        document (the expanded list row narrows `blocks` to its own statement),
        and this section is document-level and un-narrowable — the postings of
        every book would print under each currency row, the same list twice. */}
    {variant === 'sheet' &&
      document.postings &&
      document.postings.length > 0 && (
        <PostingsSection postings={document.postings} />
      )}
  </div>
);
